<template>
  <OverlayPanel
    id="slippage-settings"
    ref="overlayPanel"
    :breakpoints="{ '520px': '100vw' }"
    :show-close-icon="false"
    append-to="body"
    style="width: 310px"
    @hide="onBlur"
  >
    <div class="settings-overlay">
      <div class="settings-overlay__title">{{ $t('common.settings') }}</div>
      <div class="settings-overlay__divider" />
      <div class="settings-overlay__content">
        <div class="common-label">
          <label for="slippage">{{ $t('swap.slippageTolerance') }}:</label>
          <div @click="setPresetValue(SLIPPAGE.PRESET_OPTION_05)">0.5%</div>
          <div @click="setPresetValue(SLIPPAGE.PRESET_OPTION_10)">1.0%</div>
          <div @click="setPresetValue(SLIPPAGE.PRESET_OPTION_15)">1.5%</div>
        </div>
        <div class="slippage-options">
          <p-button
            :class="{ 'p-button-secondary': !isDefault }"
            class="slippage-options__button p-button-primary"
            label="Auto"
            @click="onAutoSlippageClick"
          />
          <input-number
            id="slippage"
            :max-fraction-digits="2"
            :min="SLIPPAGE.MIN_LEVEL_PERCENT"
            :min-fraction-digits="1"
            :model-value="_slippage"
            :step="SLIPPAGE.STEP_PERCENT"
            class="slippage-options__input"
            format
            locale="en-US"
            mode="decimal"
            placeholder="0.0"
            @blur="onBlur"
            @input="checkValue"
          />
        </div>
        <div
          :class="{ [`is-${error.type}`]: error.type }"
          class="slippage-comment"
        >
          <span v-text="error.message" />
          <div
            v-show="fromToken || toToken"
            class="settings-overlay__divider my-2"
          />
        </div>
      </div>
    </div>
  </OverlayPanel>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, onUpdated, ref, toRefs } from 'vue';
import { useI18n } from '@/composables/useI18n';
import { SLIPPAGE } from '@/constants';
import { InputNumberInputEvent } from 'primevue/inputnumber';

const { t } = useI18n();

interface IProps {
  slippage: number;
  isDefault: boolean;
  toToken?: string;
  fromToken?: string;
}

const props = defineProps<IProps>();
const emits = defineEmits(['update:slippage', 'update:isDefault']);

const overlayPanel = ref();

const error = ref<{ message?: string; type?: string }>({
  message: undefined,
  type: undefined,
});

const { isDefault, slippage } = toRefs(props);

onBeforeMount(() => {
  checkValue({ value: slippage.value * 100 });
});

onUpdated(() => {
  checkValue({ value: slippage.value * 100 });
});

const _slippage = computed({
  get: () => {
    return slippage.value ? slippage.value * 100 : 0;
  },
  set: (value) => {
    emits('update:slippage', value / 100);
  },
});

const _isDefault = computed({
  get: () => {
    return isDefault.value;
  },
  set: (value) => {
    emits('update:isDefault', value);
  },
});

function setPresetValue(value: number) {
  _slippage.value = value;
}

function onAutoSlippageClick() {
  _isDefault.value = true;
  error.value = {
    message: undefined,
    type: undefined,
  };
}

function onBlur() {
  if (error.value.type === 'error') {
    onAutoSlippageClick();
  }
}

function show(e: Event, target?: any) {
  overlayPanel?.value.show(e, target);
}

function hide() {
  overlayPanel?.value.hide();
}

function toggle(e: Event, _target?: any) {
  overlayPanel?.value.toggle(e);
}

function checkValue(event: InputNumberInputEvent | { value: number }) {
  if (!event.value) {
    return;
  }
  const _newValue =
    Math.round(((+event.value || 0) + Number.EPSILON) * 100) / 100;

  emits('update:isDefault', false);
  emits('update:slippage', _newValue / 100);

  if (_newValue === 0) {
    emits('update:isDefault', true);
    Object.assign(error.value, {
      message: undefined,
      type: undefined,
    });
    return;
  }

  if (_newValue < SLIPPAGE.FAIL_WARNING_LEVEL_PERCENT) {
    Object.assign(error.value, {
      message: t('errors.transactionMayFail'),
      type: 'warning',
    });
    return;
  }

  if (_newValue > SLIPPAGE.MAX_LEVEL_PERCENT) {
    Object.assign(error.value, {
      message: t('transaction.enterSlippage'),
      type: 'error',
    });

    return;
  }

  if (_newValue > SLIPPAGE.FRONTRUN_WARNING_LEVEL_PERCENT) {
    Object.assign(error.value, {
      message: t('errors.transactionMayBeFrontrun'),
      type: 'warning',
    });
    return;
  }

  Object.assign(error.value, {
    message: undefined,
    type: undefined,
  });
}

defineExpose({ show, hide, toggle });
</script>

<style lang="scss" scoped>
.settings-overlay {
  &__title {
    display: flex;
    flex-direction: row;
    grid-gap: 12px;
    font-size: 1rem;
    color: var(--surface-200);
    margin-bottom: 8px;
    font-weight: 500;
  }

  &__divider {
    border-bottom: 1px solid #303035;
    margin: 0 -20px;
  }

  &__content {
    padding-top: 12px;
  }
}

.slippage-options {
  display: flex;
  grid-gap: 12px;

  &__button {
    height: 32px;
    padding: 3px 16px;
    font-size: 0.875rem;
  }

  &__input {
    flex: 1;
    width: 100%;
    display: flex;
    align-items: center;
    position: relative;

    &:after {
      content: '%';
      font-weight: 600;
      color: var(--surface-600);
      font-size: 1rem;
      position: absolute;
      right: 0;
      margin-right: 8px;
    }

    ::v-deep(.p-inputnumber-input) {
      height: 32px;
      padding: 3px 24px 3px 10px;
      width: 100%;
      text-align: right;
      font-family: var(--font-family), serif;
      font-size: 15px;
      font-weight: 500;
      line-height: 22px;
      letter-spacing: -0.0043em;
    }
  }
}

.common-label {
  width: 100%;
  display: flex;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  align-items: baseline;

  label {
    margin-right: auto;
  }

  & > div {
    color: var(--surface-600);
    font-size: 0.8rem;

    margin-left: 5px;
    cursor: pointer;
    &:hover {
      color: var(--surface-500);
    }
  }
}

.slippage-label {
  background: #24243a;
  padding: 0.75rem 0.75rem;
  border: 1px solid var(--surface-card);
  transition: background-color 0.2s, color 0.2s, border-color 0.2s,
    box-shadow 0.2s;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border-radius: 6px;
  margin-right: 3px;
}
.slippage-radio:checked + .slippage-label:not(.-uncheck) {
  outline: 0 none;
  outline-offset: 0;
  box-shadow: 0 0 0 0.2rem rgb(196 181 253 / 50%);
  border-color: #c4b5fd;
}

.slippage-comment {
  text-align: right;
  height: 24px;
  font-size: 0.875rem;
  padding-top: 6px;

  &.is-error {
    color: var(--pink-600);
  }

  &.is-warning {
    color: var(--orange-600);
  }
}
</style>
